import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { getWatchlist, addToWatchlist, deleteFromWatchlist, getWatchlistItemData, importFromPortfolio } from '../../services/api';
import toast from 'react-hot-toast';
import { X, Plus, RefreshCw, Bookmark, TrendingUp, TrendingDown, Upload } from 'lucide-react';
// @ts-ignore
import Spinner from '../../components/Spinner';

interface WatchlistItem {
  ticker: string;
  companyName?: string;
  company_name?: string;
  price?: number;
  current_price?: number;
  change?: number;
  changePercent?: number;
  change_percent?: number;
  sparkline?: number[];
  last_updated?: string;
  volume?: number;
  avg_volume?: number;
  week52_high?: number;
  week52_low?: number;
  market_cap?: number;
}

const SparklineChart: React.FC<{ data: number[] }> = ({ data }) => {
  if (!data || data.length < 2) return <div className="h-10 w-24 bg-slate-50 rounded"></div>;
  
  const width = 120;
  const height = 40;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min === 0 ? 1 : max - min;
  const points = data.map((d, i) => 
    `${(i / (data.length - 1)) * width},${height - ((d - min) / range) * height}`
  ).join(' ');
  
  const isUp = data[data.length - 1] >= data[0];
  const color = isUp ? '#10b981' : '#ef4444';

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const WatchlistView: React.FC = () => {
  const { currentUser } = useAuth();
  const { portfolios } = usePortfolio();
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newTicker, setNewTicker] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedPortfolioForImport, setSelectedPortfolioForImport] = useState<string>('');

  const fetchWatchlistData = useCallback(async () => {
    if (!currentUser) {
      setWatchlist([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const data = await getWatchlist(token);
      setWatchlist(data.watchlist || []);
    } catch (error: any) {
      toast.error(`Could not load watchlist: ${error.message}`);
      setWatchlist([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchWatchlistData();
  }, [fetchWatchlistData]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicker.trim() || !currentUser) return;
    
    setIsAdding(true);
    const loadingToast = toast.loading(`Adding ${newTicker.toUpperCase()}...`);
    
    try {
      const token = await currentUser.getIdToken();
      await addToWatchlist(token, newTicker.toUpperCase().trim());
      toast.success(`Added ${newTicker.toUpperCase()} to watchlist`, { id: loadingToast });
      setNewTicker('');
      await fetchWatchlistData();
    } catch (error: any) {
      toast.error(`Failed to add: ${error.message}`, { id: loadingToast });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (ticker: string) => {
    if (!currentUser) return;
    if (!window.confirm(`Remove ${ticker} from watchlist?`)) return;
    
    const loadingToast = toast.loading('Removing...');
    try {
      const token = await currentUser.getIdToken();
      const userId = currentUser.uid;
      await deleteFromWatchlist(token, userId, ticker);
      toast.success(`Removed ${ticker}`, { id: loadingToast });
      await fetchWatchlistData();
    } catch (error: any) {
      toast.error(`Failed to remove: ${error.message}`, { id: loadingToast });
    }
  };

  const handleClearAll = async () => {
    if (!currentUser || watchlist.length === 0) return;
    
    const confirmed = window.confirm(
      `⚠️ WARNING: This will remove ALL ${watchlist.length} tickers from your watchlist!\n\n` +
      'This action CANNOT be undone.\n\n' +
      'Are you absolutely sure you want to continue?'
    );
    if (!confirmed) return;

    // Second confirmation for safety
    const doubleConfirmed = window.confirm(
      '🚨 FINAL CONFIRMATION\n\n' +
      'This will permanently clear your entire watchlist.\n' +
      'Click OK to proceed.'
    );
    if (!doubleConfirmed) return;

    const loadingToast = toast.loading(`Clearing ${watchlist.length} items...`);
    try {
      const token = await currentUser.getIdToken();
      const userId = currentUser.uid;
      
      // Delete each ticker
      for (const item of watchlist) {
        await deleteFromWatchlist(token, userId, item.ticker);
      }
      
      toast.success(`Successfully cleared ${watchlist.length} items from watchlist`, { id: loadingToast });
      await fetchWatchlistData();
    } catch (error: any) {
      toast.error(`Failed to clear watchlist: ${error.message}`, { id: loadingToast });
    }
  };

  const handleRefreshPrices = async () => {
    if (!currentUser || watchlist.length === 0) return;
    
    setIsRefreshing(true);
    const loadingToast = toast.loading('Updating prices...');
    
    try {
      const token = await currentUser.getIdToken();
      // Fetch updated data for all watchlist items
      const promises = watchlist.map(item => getWatchlistItemData(token, item.ticker));
      await Promise.all(promises);
      
      toast.success('Prices updated!', { id: loadingToast });
      await fetchWatchlistData();
    } catch (error: any) {
      toast.error(`Update failed: ${error.message}`, { id: loadingToast });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleImportFromPortfolio = async () => {
    // Show modal to select portfolio
    setShowImportModal(true);
  };

  const executeImport = async () => {
    if (!currentUser || !selectedPortfolioForImport) {
      toast.error('Please select a portfolio to import from.');
      return;
    }
    
    setIsImporting(true);
    setShowImportModal(false);
    const loadingToast = toast.loading('Importing tickers from portfolio...');
    
    try {
      const token = await currentUser.getIdToken();
      // Call the backend with the selected portfolio ID
      const response = await importFromPortfolio(token, selectedPortfolioForImport);
      toast.success(response.message || 'Portfolio tickers imported!', { id: loadingToast });
      await fetchWatchlistData();
      setSelectedPortfolioForImport('');
    } catch (error: any) {
      toast.error(`Import failed: ${error.message}`, { id: loadingToast });
    } finally {
      setIsImporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 md:p-8 bg-slate-50">
      {/* Portfolio Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Select Portfolio to Import</h3>
            <p className="text-sm text-slate-600 mb-4">
              Choose a portfolio to import all its tickers into your watchlist.
            </p>
            
            <div className="space-y-2 mb-6">
              {portfolios.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No portfolios available</p>
              ) : (
                portfolios.map((portfolio) => (
                  <label
                    key={portfolio.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all"
                  >
                    <input
                      type="radio"
                      name="portfolioImport"
                      value={portfolio.id}
                      checked={selectedPortfolioForImport === portfolio.id}
                      onChange={(e) => setSelectedPortfolioForImport(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div>
                      <div className="font-medium text-slate-900">{portfolio.name}</div>
                      {portfolio.description && (
                        <div className="text-xs text-slate-500">{portfolio.description}</div>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setSelectedPortfolioForImport('');
                }}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={executeImport}
                disabled={!selectedPortfolioForImport}
                className={`px-4 py-2 rounded-lg font-medium ${
                  selectedPortfolioForImport
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                Import Tickers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-6 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Bookmark className="text-blue-600" size={32} />
            Watchlist
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Track stocks you're interested in with real-time prices
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={handleImportFromPortfolio}
            disabled={isImporting}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
              isImporting
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-purple-600 hover:border-purple-300'
            }`}
          >
            <Upload size={16} />
            {isImporting ? 'Importing...' : 'Import Portfolio'}
          </button>
          
          {watchlist.length > 0 && (
            <button 
              onClick={handleRefreshPrices}
              disabled={isRefreshing}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                isRefreshing
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Updating...' : 'Refresh Prices'}
            </button>
          )}

          {watchlist.length > 0 && (
            <button 
              onClick={handleClearAll}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-red-700 transition-all"
            >
              <X size={16} />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Add Ticker Form */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
        <form onSubmit={handleAdd} className="flex gap-3">
          <input
            type="text"
            value={newTicker}
            onChange={(e) => setNewTicker(e.target.value)}
            placeholder="Enter ticker symbol (e.g., AAPL)"
            className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isAdding}
          />
          <button
            type="submit"
            disabled={isAdding || !newTicker.trim()}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
              isAdding || !newTicker.trim()
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
            }`}
          >
            <Plus size={16} />
            Add Stock
          </button>
        </form>
      </div>

      {/* Watchlist Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        {watchlist.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Symbol</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Price</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Chg %</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider hidden md:table-cell">7D Trend</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider hidden lg:table-cell">Volume</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider hidden xl:table-cell">52W Range</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {watchlist.map((item) => {
                  const price = item.price ?? item.current_price;
                  const chgPct = item.changePercent ?? item.change_percent ?? 0;
                  const isUp = chgPct >= 0;
                  const vol = item.volume;
                  const avgVol = item.avg_volume;
                  const volRatio = vol && avgVol && avgVol > 0 ? vol / avgVol : null;
                  const hi52 = item.week52_high;
                  const lo52 = item.week52_low;
                  const rangePct = hi52 && lo52 && price && hi52 > lo52
                    ? ((price - lo52) / (hi52 - lo52)) * 100
                    : null;
                  const fmtVol = (v?: number) => {
                    if (!v) return '--';
                    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
                    return String(v);
                  };
                  return (
                    <tr key={item.ticker} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900 text-sm">{item.ticker}</div>
                        <div className="text-xs text-slate-400 truncate max-w-[140px]">{item.companyName || item.company_name || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-semibold text-slate-900 font-mono text-sm">
                          {price != null ? `$${price.toFixed(2)}` : '--'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded ${
                          isUp ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                        }`}>
                          {isUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                          {isUp ? '+' : ''}{chgPct.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex justify-center">
                          <SparklineChart data={item.sparkline || []} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right hidden lg:table-cell">
                        <div className="text-xs font-mono text-slate-700">{fmtVol(vol)}</div>
                        {volRatio != null && (
                          <div className={`text-[11px] font-medium ${volRatio > 1.5 ? 'text-amber-600' : 'text-slate-500'}`}>
                            {volRatio.toFixed(1)}x avg
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        {rangePct != null ? (
                          <div className="w-28 mx-auto">
                            <div className="flex justify-between text-[11px] text-slate-600 mb-0.5 font-mono font-medium">
                              <span>{lo52?.toFixed(0)}</span>
                              <span>{hi52?.toFixed(0)}</span>
                            </div>
                            <div className="h-1.5 bg-slate-200 rounded-full relative">
                              <div
                                className="absolute top-0 w-2 h-2 rounded-full -mt-0.5 bg-blue-500 border-2 border-white shadow"
                                style={{ left: `calc(${Math.min(Math.max(rangePct, 2), 98)}% - 4px)` }}
                              />
                            </div>
                            <div className="text-center text-[11px] text-slate-500 mt-0.5 font-medium">{rangePct.toFixed(0)}% of range</div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 block text-center">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(item.ticker)}
                          className="p-1.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Remove from watchlist"
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-slate-500">
            <Bookmark size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-700 mb-2">Your watchlist is empty</p>
            <p className="text-sm">Add stocks to track their performance</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistView;
